import React, { useContext, useEffect, useMemo, useState } from "react"
import { NewsContext, LanguageContext } from "../context"
import moment from "moment"
import { DATE_FORMAT, DATE_TIME_FORMAT } from "../utils/constants"
import { Link } from "react-router-dom"


const News = () => {
  const { news = [], newsLoaded, newsCategories = [], newsCategoriesLoaded, newsLoading, moreNewsAvailable, fetchMoreNews } = useContext(NewsContext)
  const { t, localizedProperty, currentLocale } = useContext(LanguageContext)

  const isLoading = !newsLoaded || !newsCategoriesLoaded

  const [activeCategory, setActiveCategory] = useState("all")

  useEffect(() => {
    if (!newsCategories.some((category) => category.id === activeCategory)) {
      setActiveCategory("all")
    }
  }, [newsCategories, activeCategory])

  const sortedNews = useMemo(() => {
    return [...news].sort((a, b) => {
      const dateA = moment(a.date, 'YYYY-MM-DD')
      const dateB = moment(b.date, 'YYYY-MM-DD')
      if (dateA && dateB) {
        return dateB.valueOf() - dateA.valueOf()
      }
      if (dateA) {
        return -1
      }
      if (dateB) {
        return 1
      }
      return 0
    })
  }, [news])

  const filteredNews = useMemo(() => {
    const filteredNews = sortedNews.filter((newsItem) => {
      // filter out news which are published for currently selected locale
      if (newsItem.locales && newsItem.locales.length > 0) {
        return newsItem.locales.includes(currentLocale)
      }
    })
    if (activeCategory === "all") {
      return filteredNews
    }
    return filteredNews.filter((newsItem) => {
        return newsItem.categories?.includes(activeCategory)
    })
  }, [sortedNews, activeCategory, currentLocale])

  return (
    <div className="news-page relative">
      {isLoading && (
        <div className="global-loading">
          <div className="global-loading__background" />
          <div className="global-loading__spinner" />
        </div>
      )}

      <div className="content-parent news-page__container">
        <header className="news-page__header">
          <h1 className="news-page__title">{t("news.title", null, null, "News")}</h1>

          {newsCategories.length > 1 && (
            <div className="news-page__filters">
              <button
                type="button"
                className={`news-page__filter${activeCategory === "all" ? " news-page__filter--active" : ""}`}
                onClick={() => setActiveCategory("all")}
              >
                {t("news_category.all")}
              </button>
              {newsCategories.map((category) => {
                const isActive = category.id === activeCategory
                return (
                  <button
                    key={category.id}
                    type="button"
                    className={`news-page__filter${isActive ? " news-page__filter--active" : ""}`}
                    onClick={() => setActiveCategory(category.id)}
                  >
                    {localizedProperty(category, "name")}
                  </button>
                )
              })}
            </div>
          )}
        </header>

        <section className="news-page__list">
          {!isLoading && filteredNews.length === 0 && (
            <p className="news-page__empty-message">
              {t("news.no_news")}
            </p>
          )}

          {filteredNews.map((newsItem, index) => {
            const title = localizedProperty(newsItem, "title")
            const subtitle = localizedProperty(newsItem, "subtitle")
            const date = moment(newsItem.date, 'YYYY-MM-DD')
            const formattedDate = date ? date.format(DATE_FORMAT) : ""
            const imageUrl = newsItem.image?.url
            const imageAlt = newsItem.image?.caption || localizedProperty(newsItem, "title")
            const key = newsItem.id

            return (
              <article className="news-card" key={key}>
                <div className="news-card__media">
                  {imageUrl ? (
                    <Link to={`/news/${newsItem.id}`}>
                      <img className="news-card__image" src={imageUrl} alt={imageAlt} loading="lazy" />
                    </Link>
                  ) : (
                    <div className="news-card__image news-card__image--placeholder" aria-hidden="true" />
                  )}
                </div>

                <div className="news-card__body">
                  <Link to={`/news/${newsItem.id}`}>
                    <h2 className="news-card__title">{title}</h2>
                  </Link>
                  {formattedDate && <p className="news-card__date">{formattedDate}</p>}
                  {subtitle && <p className="news-card__subtitle">{subtitle}</p>}
                  <div className="news-card__tags">
                    {newsItem.categories?.map((categoryId) => {
                      const category = newsCategories.find((cat) => cat.id === categoryId)
                      if (!category) {
                        return null
                      }
                      const categoryLabel = localizedProperty(category, "name")
                      return categoryLabel ? (
                        <span key={categoryId} className="news-card__tag">{categoryLabel}</span>
                      ) : null
                    })}
                  </div>
                </div>
              </article>
            )
          })}
          {moreNewsAvailable ? (
            <div className="news-page__load-more">
              <button onClick={fetchMoreNews} className={`btn-loading ${newsLoading ? 'running' : ''}`}>
                <div className="loading"></div>
                {t('news.load_more')}
              </button>
            </div>
          ) : 
            filteredNews.length > 0 && (
              <p className="news-page__end-message">
                {t("news.no_more_news")}
              </p>
            )
          }
        </section>
      </div>
    </div>
  )
}

export default News
